import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { AlertTriangle, CheckCircle2, Plus, Link as LinkIcon, Loader2 } from 'lucide-react'
import type { Insights } from '../hooks/useInsights'

interface ZeroResultsActionableProps {
  queries: Insights['zeroResults'] | undefined
  isLoading: boolean
}

export function ZeroResultsActionable({ queries, isLoading }: ZeroResultsActionableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Gaps in the Catalogue
        </CardTitle>
        <CardDescription>
          Searches that found nothing — fix with a synonym, redirect, or by adding products
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !queries || queries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle2 className="mb-2 h-10 w-10 text-emerald-500/70" />
            <p className="text-sm text-muted-foreground">No zero-result queries</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Every search matched something in this period
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {queries.slice(0, 15).map((q) => (
              <ZeroResultRow key={q.query} row={q} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ZeroResultRow({ row }: { row: Insights['zeroResults'][number] }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-amber-500/5 p-2.5 pr-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">"{row.query}"</div>
        <div className="text-xs text-muted-foreground">
          {row.count} search{row.count === 1 ? '' : 'es'}
          {row.hasSynonym && ' · synonym exists'}
          {row.hasRedirect && ' · redirect exists'}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <SynonymDialog query={row.query} disabled={row.hasSynonym} />
        <RedirectDialog query={row.query} disabled={row.hasRedirect} />
      </div>
    </div>
  )
}

function SynonymDialog({ query, disabled }: { query: string; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState(query)
  const [synonymsText, setSynonymsText] = useState('')
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: async () => {
      const synonyms = synonymsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      if (synonyms.length === 0) throw new Error('Add at least one synonym')

      const response = await fetch('/api/synonyms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          term,
          synonymList: synonyms,
          enabled: 1,
          originType: 'manual',
        }),
      })
      if (!response.ok) throw new Error(await response.text())
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insights'] })
      setOpen(false)
      setSynonymsText('')
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled} className="h-7 text-xs">
          <Plus className="mr-1 h-3 w-3" />
          Synonym
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add synonym for "{query}"</DialogTitle>
          <DialogDescription>
            Map this search term to related words already in the catalogue. For example, "red" could
            map to "crimson, scarlet, burgundy".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-sm font-medium">Term</label>
            <Input value={term} onChange={(e) => setTerm(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium">Synonyms (comma separated)</label>
            <Input
              value={synonymsText}
              onChange={(e) => setSynonymsText(e.target.value)}
              placeholder="crimson, scarlet, burgundy"
              className="mt-1"
            />
          </div>
          {createMutation.error && (
            <p className="text-xs text-destructive">
              {(createMutation.error as Error).message || 'Failed to create synonym'}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create synonym
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RedirectDialog({ query, disabled }: { query: string; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!url) throw new Error('Add a redirect URL')
      const response = await fetch('/api/redirects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          queryMatch: query,
          queryMatchType: 'exact',
          redirectUrl: url,
          enabled: 1,
        }),
      })
      if (!response.ok) throw new Error(await response.text())
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insights'] })
      setOpen(false)
      setUrl('')
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled} className="h-7 text-xs">
          <LinkIcon className="mr-1 h-3 w-3" />
          Redirect
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redirect "{query}"</DialogTitle>
          <DialogDescription>
            Send this search straight to a specific page (e.g. a category, brand page, or blog
            article that answers the query).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-sm font-medium">Redirect URL</label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://birchcreative.com.au/product-category/..."
              className="mt-1"
            />
          </div>
          {createMutation.error && (
            <p className="text-xs text-destructive">
              {(createMutation.error as Error).message || 'Failed to create redirect'}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create redirect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
